import React, { useContext, useEffect, useState } from 'react'
import {DatePicker, Typography, Space, App, Radio, Divider} from 'antd'
import { Helmet } from "react-helmet";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    defaults
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import dayjs, {Dayjs} from 'dayjs'
import { API, Enum, Variable } from "../../../resources/constants";
import language from "../../../resources/languages/en_US"

// Interfaces
import { Tender as ReportTendersType } from "../../../interfaces/pages/report"
import { AxiosError } from "../../../interfaces/axios"


// Contexts
import AxiosContext from "../../../contexts/axios";

const { App: { title }, Pages: { Report: { Tender: TenderLanguage }  }, Share, Validation, Message: MessageLanguage, Enum: EnumLanguage } = language
const { TENDER_TENDER_REPORT } = API
const { Message, Country } = Enum
const { PostedDate } = Variable;

ChartJS.defaults.font.family = "'Inter', 'Arial', sans-serif";
ChartJS.defaults.font.size = 14;
ChartJS.defaults.font.weight = 'normal';
ChartJS.defaults.color = '#000000';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const currency = new Intl.NumberFormat()

const defaultReport = {
    tenders: {
        all: 0,
        published: 0,
        hasFile: 0,
        readableFile: 0
    },
    userTenders: {
        all: 0,
        hasFile: 0,
        readableFile: 0,
        analyzed: 0,
        eligible: 0,
        furtherInvestigationRequired: 0,
        notEligible: 0,
        felixFusionPick: 0
    }
}

export default function Tender() {

    const [country, setCountry] = useState<string>(Country.USA);
    const [postedDate, setPostedDate] = useState<[Dayjs, Dayjs]>(PostedDate);
    const [report, setReport] = useState<ReportTendersType>(defaultReport);

    const { axios } = useContext(AxiosContext)
    const { message } = App.useApp()

    useEffect(() => {
        getReport();
    }, [country, postedDate]);

    const getReport = () => axios.post(TENDER_TENDER_REPORT, { from: dayjs(postedDate[0]).format('YYYY-MM-DD'), to: dayjs(postedDate[1]).format('YYYY-MM-DD') }, { params: { source: country } }).then((response: { data: ReportTendersType }) => {
        const { data: report } = response;
        setReport(report);
    }, async (error: AxiosError) => {
        const { response: { data } } = error;
        setReport(defaultReport);
        // @ts-ignore
        message.error(MessageLanguage[data.message]||data.message).then(response => response )
    })


    return (
        <div className={'report-tenders'}>
            <Helmet>
                <title>{title} - {TenderLanguage.title}</title>
            </Helmet>
            <Space direction={'vertical'} size={30} style={{width:'100%',textAlign:'center'}}>
                <Space size={5}>
                    <Radio.Group value={country} onChange={({target: { value: country }}) => setCountry(country)} buttonStyle={'solid'} className={'radio-group-style-two'}>
                        <Radio.Button value={Country.USA}>
                            <img
                                alt={EnumLanguage.Country[Country.USA]}
                                src={require('../../../resources/media/flag/usa-square.jpg')}
                                width={32}
                                height={32}
                            />
                        </Radio.Button>
                        <Radio.Button value={Country.Canada}>
                            <img
                                alt={EnumLanguage.Country[Country.Canada]}
                                src={require('../../../resources/media/flag/canada-square.jpg')}
                                width={32}
                                height={32}
                            />
                        </Radio.Button>
                        <Radio.Button value={Country.Europe} disabled={true}>
                            <img
                                alt={EnumLanguage.Country[Country.Europe]}
                                src={require('../../../resources/media/flag/europe-square.jpg')}
                                width={32}
                                height={32}
                            />
                        </Radio.Button>
                    </Radio.Group>
                    <Divider type="vertical" style={{height:'2em'}} />
                    <Space size={15}><Typography.Text strong>{TenderLanguage.postedDate}</Typography.Text><DatePicker.RangePicker
                        allowClear={false}
                        size={'large'}
                        format="YYYY-MM-DD"
                        // @ts-ignore
                        value={postedDate}
                        // @ts-ignore
                        onChange={(postedDate) => setPostedDate(postedDate)}
                    /></Space>
                </Space>
                <Bar
                    data={{
                        labels: [''],
                        datasets: [
                            {
                                label: `${TenderLanguage.allTenders} (${currency.format(report?.tenders?.all)})`,
                                data: [report?.tenders?.all],
                                backgroundColor: 'rgba(255, 99, 132, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.publishedTenders} (${currency.format(report?.tenders?.published)})`,
                                data: [report?.tenders?.published],
                                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.hasFileTenders} (${currency.format(report?.tenders?.hasFile)})`,
                                data: [report?.tenders?.hasFile],
                                backgroundColor: 'rgba(255, 206, 86, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.readableFileTenders} (${currency.format(report?.tenders?.readableFile)})`,
                                data: [report?.tenders?.readableFile],
                                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userAllTenders} (${currency.format(report?.userTenders?.all)})`,
                                data: [report?.userTenders?.all],
                                backgroundColor: 'rgba(153, 102, 255, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userHasFileTenders} (${currency.format(report?.userTenders?.hasFile)})`,
                                data: [report?.userTenders?.hasFile],
                                backgroundColor: 'rgba(255, 159, 64, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userReadableFileTenders} (${currency.format(report?.userTenders?.readableFile)})`,
                                data: [report?.userTenders?.readableFile],
                                backgroundColor: 'rgba(0, 128, 0, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userAnalyzedTenders} (${currency.format(report?.userTenders?.analyzed)})`,
                                data: [report?.userTenders?.analyzed],
                                backgroundColor: 'rgba(128, 0, 128, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userEligibleTenders} (${currency.format(report?.userTenders?.eligible)})`,
                                data: [report?.userTenders?.eligible],
                                backgroundColor: 'rgba(0, 191, 255, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userFurtherInvestigationRequiredTenders} (${currency.format(report?.userTenders?.furtherInvestigationRequired)})`,
                                data: [report?.userTenders?.furtherInvestigationRequired],
                                backgroundColor: 'rgba(220, 20, 60, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userNotEligibleTenders} (${currency.format(report?.userTenders?.notEligible)})`,
                                data: [report?.userTenders?.notEligible],
                                backgroundColor: 'rgba(255, 140, 0, 0.6)',
                            },
                            {
                                label: `${TenderLanguage.userFelixFusionPickTenders} (${currency.format(report?.userTenders?.felixFusionPick)})`,
                                data: [report?.userTenders?.felixFusionPick],
                                backgroundColor: 'rgba(46, 139, 87, 0.6)',
                            },
                        ],
                    }}
                    options={{
                        responsive: true,
                        plugins: {
                            legend: {
                                position: 'top' as const,
                            },
                            title: {
                                display: true,
                                text: TenderLanguage.title,
                            },
                            tooltip: {
                                callbacks: {
                                    label: function (context: any) {
                                        return `${context.dataset.label}`;
                                    },
                                },
                            },
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: {
                                    stepSize: 10,
                                    callback: function (value: any) {
                                        return Number.isInteger(value) ? value : null;
                                    },
                                },
                            },
                        },
                    }}/>
            </Space>
        </div>
    )
}
